import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = 'trialBannerEmail';
const DISMISS_KEY = 'trialBannerDismissed';

/**
 * Trial countdown banner — renders at the top of admin pages when the user's
 * email (stored in localStorage as `trialBannerEmail`) has an active trial.
 *
 * To activate for a user, the trial start flow sets:
 *   localStorage.setItem('trialBannerEmail', <email>)
 *
 * Per-trial-id dismissals are remembered in `trialBannerDismissed` so the
 * banner doesn't keep nagging once acknowledged.
 */
const TrialBanner = () => {
  const [trial, setTrial] = useState(null);

  useEffect(() => {
    const email = (typeof window !== 'undefined' && localStorage.getItem(STORAGE_KEY)) || '';
    if (!email) return;
    let cancelled = false;
    axios.get(`${API}/subscriptions/trial/status/${encodeURIComponent(email)}`)
      .then((r) => {
        if (cancelled) return;
        const dismissed = JSON.parse(localStorage.getItem(DISMISS_KEY) || '[]');
        const active = (r.data?.trials || [])
          .filter((t) => !t.ended && !dismissed.includes(t.id))
          .sort((a, b) => a.days_remaining - b.days_remaining);
        setTrial(active[0] || null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!trial) return null;

  const days = trial.days_remaining;
  const tierName = trial.tier_meta?.name || trial.tier;
  const monthly = trial.tier_meta?.monthly_amount;
  const isFinalStretch = days <= 2;
  const dismiss = () => {
    const list = JSON.parse(localStorage.getItem(DISMISS_KEY) || '[]');
    if (!list.includes(trial.id)) list.push(trial.id);
    localStorage.setItem(DISMISS_KEY, JSON.stringify(list));
    setTrial(null);
  };

  return (
    <div
      data-testid="trial-countdown-banner"
      className={`w-full flex items-center gap-3 px-4 py-2 text-sm border-b ${
        isFinalStretch
          ? 'bg-red-500/15 border-red-500/30 text-red-200'
          : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200'
      }`}
    >
      <Sparkles className="w-4 h-4 shrink-0" />
      <div className="flex-1">
        <span className="font-semibold" data-testid="trial-days-remaining">
          {days === 0 ? 'Last day' : `${days} day${days === 1 ? '' : 's'} left`}
        </span>{' '}
        on your <strong>{tierName}</strong> free trial
        {monthly ? <> · ${monthly.toLocaleString()}/mo after</> : null}.{' '}
        <a
          href="/pricing"
          className="underline font-medium"
          data-testid="trial-banner-cta"
        >
          {isFinalStretch ? 'Add a card to keep going →' : 'Lock in founder pricing →'}
        </a>
      </div>
      <button
        onClick={dismiss}
        className="opacity-70 hover:opacity-100"
        aria-label="Dismiss"
        data-testid="trial-banner-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default TrialBanner;
